import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTable, useFlexLayout, useSortBy } from 'react-table';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Loading } from 'components';

const Table = ({
  columns,
  data,
  onRowClick,
  isLoading,
  isRowClickable,
  hiddenColumns,
  onSort,
  sortByInfo,
  disableColumnIDList,
}) => {
  const getStyles = (props, align = 'left') => [
    props,
    {
      style: {
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        display: 'flex',
      },
    },
  ];
  // const headerProps = (props, { column }) => getStyles(props, column.align);
  const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

  const defaultColumn = useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: 30, // minWidth is only used as a limit for resizing
      width: 150, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    []
  );

  const memoizedColumns = useMemo(() => columns, [columns]);

  const memoizedData = useMemo(() => data, [data]);

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns: memoizedColumns,
      data: memoizedData,
      defaultColumn,
      initialState: { hiddenColumns: hiddenColumns },
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      //disable Sorting for empty data
      disableSortBy: isLoading || data.length === 0,
    },
    useFlexLayout,
    useSortBy
  );

  useEffect(() => {
    sortBy && sortBy.length && !disableColumnIDList.includes(sortBy[0].id) && onSort(sortBy);
    // eslint-disable-next-line
  }, [onSort, sortBy]);

  return (
    <div className="card card--scrollable">
      <div {...getTableProps()} className="lf-table">
        {headerGroups.map((headerGroup, headerKey) => (
          <div key={headerKey} {...headerGroup.getHeaderGroupProps()} className="lf-table__head-row">
            {headerGroup.headers.map((column, columnKey) => (
              <div
                key={columnKey}
                {...column.getHeaderProps(column.getSortByToggleProps({ title: '' }))}
                className={`lf-table__col lf-table__col--head ${column.className}`}
              >
                {column.render('Header')}

                {column.sortable && !disableColumnIDList.includes(column.id) && (
                  <span className="lf-table__sort">
                    {(column.id !== sortByInfo.orderBy ||
                      (column.id === sortByInfo.orderBy && sortByInfo.order === 'asc')) && (
                      <span className="lf-table__sort--asc">
                        <FaCaretUp />
                      </span>
                    )}
                    {(column.id !== sortByInfo.orderBy ||
                      (column.id === sortByInfo.orderBy && sortByInfo.order === 'desc')) && (
                      <span className="lf-table__sort--desc">
                        <FaCaretDown />
                      </span>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
        {isLoading ? (
          <div className="lf-table__loading-container">
            <Loading />
          </div>
        ) : (
          <div className="lf-table__body">
            {rows.length !== 0 &&
              rows.map((row, rowKey) => {
                prepareRow(row);
                return (
                  <div
                    key={rowKey}
                    {...row.getRowProps()}
                    className={`lf-table__row ${isRowClickable ? 'lf-table__row--no-cursor' : ''}`}
                    onClick={() => {
                      onRowClick(row.original.id);
                    }}
                  >
                    {row.cells.map((cell, cellKey) => {
                      return (
                        <div
                          key={cellKey}
                          {...cell.getCellProps(cellProps)}
                          className={`lf-table__col ${cell.column.className}`}
                        >
                          {cell.render('Cell')}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            {rows.length === 0 && <p className="lf-text-center pt-5x pb-5x">No results found</p>}
          </div>
        )}
      </div>
    </div>
  );
};

Table.propTypes = {
  onRowClick: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isRowClickable: PropTypes.bool,
  hiddenColumns: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onSort: PropTypes.func,
  sortByInfo: PropTypes.shape({
    orderBy: PropTypes.string.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }),
  disableColumnIDList: PropTypes.array.isRequired,
};

Table.defaultProps = {
  onRowClick: () => {},
  data: [],
  columns: [{}],
  isLoading: false,
  isRowClickable: true,
  hiddenColumns: '',
  onSort: () => {},
};

export default Table;
